<template>
  <v-layout
    column
    class="reference-image-wrapper image-with-palette"
    :class="{'has-palette': isMultiview || (hasPalette && showOverlay)}">
    <v-flex 
      v-if="showPlaycontrol"
      shrink
      :order-0="playbackTop"
      :order-2="!playbackTop"
      class="slice-control-div">
      <oct-slice-controls
        :index="sliceCurrentIndex || 0"
        :count="slices ? slices.length : 1"
        :playRate="playRate"
        :isPlaying="isPlaying"
        @previous="$emit('previous')"
        @next="$emit('next')"
        @play="$emit('play')"
        @pause="$emit('pause')"
        @setPlayRate="$emit('setPlayRate', $event)"/>
    </v-flex>
    <v-flex order-1 grow class="outer-image-wrapper">
      <v-row no-gutters class="inner-image-wrapper">
        <v-col
          :id="referenceId"
          class="reference-canvas-wrapper image interactive-canvas-wrapper"
          :class="wrapperClass"
          ref="octReference"
          @wheel.exact="handleWheel"
          @mousedown.self="handleMousedown"
          @mousemove.self="handleMousemove"
          @mouseup.self.left="handleMouseUp"
          @mouseout.self="handleMouseout"
          v-hammer:press="handlePanstart"
          v-hammer:panstart="handlePanstart"
          v-hammer:pan="handlePanmove"
          v-hammer:panend="handleMouseUp"
          v-hammer:tap="handleTap">
          <image-not-found v-if="refImgNotFound" />
          <canvas-image
            :img="refImg"
            :imgSize="refFit"
            :canvasSize="refFit" />
          <pixel-shaded-canvas
            v-if="overlayImg && showOverlay"
            :img="overlayImg"
            :imgSize="refFit"
            :canvasSize="refFit"
            :thicknessMapColorSettings="overlayColorSettings"
            :bounds="sliceBounds"
            :style="{opacity: overlayOpacity}"
            :componentName="componentName"/>
          <canvas-annotation
            v-if="overlayAnalysis && showOverlay && showOverlayETDRS"
            :canvasSize="refFit"
            :annotation="etdrs"/>
          <canvas-annotation 
            v-if="isSpectralisCircle"
            :canvasSize="refFit" 
            :imgSize="refFit" 
            :annotation="circularPattern" />
          <canvas-annotation 
            v-if="isSpectralisRadialCircle"
            :canvasSize="refFit" 
            :imgSize="refFit" 
            :annotation="radialCirclesPattern" />
          <canvas-annotation 
            v-else
            :canvasSize="refFit" 
            :imgSize="refFit" 
            :annotation="sliceSelector" />
          <v-progress-circular v-if="isLoading" class="escalon-absolute-center" indeterminate />
        </v-col>
        <v-col
          class="xs2 thickness-map-palette-wrapper palette"
          :id="paletteId"
          v-if="hasPalette && showOverlay">
          <canvas-annotation
            :canvasSize="paletteSize"
            :imgSize="paletteSize"
            :annotation="paletteAnnotation"/>
        </v-col>
      </v-row>
    </v-flex>
  </v-layout>
</template>

<script>
import { POINTER_CLASSES, Directions } from "../../../config/config";
import canvasImage from "../../../canvas/components/CanvasImage";
import pixelShadedCanvas from "../../../canvas/components/PixelShadedCanvas";
import canvasAnnotation from "../../../canvas/components/CanvasAnnotation";
import SliceSelector from "../../../canvas/annotations/sliceSelector";
import CircularPattern from "../../../canvas/annotations/CircularPattern";
import RadialCirclesPattern from "../../../canvas/annotations/radialCirclesPattern";
import octSliceControls from "./OCTSliceControls";
import ThicknessMapPalette from "../../../canvas/annotations/thicknessMapPalette";
import AnalysisETDRS from "../../../canvas/annotations/analysisETDRS";
import touchMouseEvents from "../../mixins/TouchMouseEvents";
import imageNotFound from "../../misc/ImageNotFound";
import { viewOffset, withinViewBounds, outOfBoundsDirection} from "../../../canvas/geometry";

export default {
  name: "octReferenceImage",
  mixins: [touchMouseEvents],
  components: {
    canvasImage,
    canvasAnnotation,
    octSliceControls,
    pixelShadedCanvas,
    imageNotFound
  },
  props: {
    playbackTop: { type: Boolean, default: false },
    refImg: HTMLImageElement,
    refFit: Object,
    refSize: Object,
    refImgLocation: Function,
    isLoading: Boolean,
    refImgNotFound: Boolean,
    slices: Array,
    sliceCurrent: Object,
    sliceBounds: Object,
    sliceCurrentIndex: Number,
    composites: Array,
    compositeCurrent: Object,
    compositeBounds: Object,
    isPlaying: Boolean,
    playRate: String,
    showOverlay: Boolean,
    overlayOpacity: Number,
    overlayImg: [HTMLCanvasElement, HTMLImageElement],
    overlaySize: Object,
    overlayColorSettings: Object,
    overlayAnalysis: Object,
    overlayFoveaOffset: Object,
    showOverlayETDRS: Boolean,
    palette: Array,
    paletteSize: Object,
    paletteMin: Number,
    paletteMax: Number,
    paletteStep: Number,
    referenceId: { type: String, default: "reference-image-wrapper" },
    paletteId: { type: String, default: "reference-palette-wrapper" },
    drawAllSlices: Boolean,
    showExtension: Boolean,
    showBindingBox: Boolean,
    isSpectralisCircle: Boolean,
    isSpectralisRadial: Boolean,
    isSpectralisRadialCircle: Boolean,
    isMultiview: Boolean
  },
  data() {
    return {
      sliceSelector: new SliceSelector(),
      paletteAnnotation: new ThicknessMapPalette(),
      circularPattern: new CircularPattern(),
      radialCirclesPattern: new RadialCirclesPattern(),
      etdrs: new AnalysisETDRS("black", "black"),
      cursorClass: "",
      disableCustomEvents: false,
      componentName: "OCTReferenceImage",
      outOfBounds: Directions.Unknown
    };
  },
  computed: {
    showPlaycontrol(){
      return this.slices && this.slices.length > 1;
    },
    wrapperClass() {
      let classes = {};
      if (this.cursorClass) classes[this.cursorClass] = true;
      return classes;
    },
    hasPalette() {
      return this.palette && this.palette.length;
    },
    boundingBoxSize(){
      if (this.isSpectralisRadialCircle) 
        return{
          w: this.radialCirclesPattern.drawBoundingBoxWidth,
          h: this.radialCirclesPattern.drawBoundingBoxHeight
        }
      else
        return{
          w: this.sliceSelector.drawBoundingBoxWidth,
          h: this.sliceSelector.drawBoundingBoxHeight
        }
    }
  },
  methods: {
    handleMouseUp(){
      this.outOfBounds = Directions.Unknown;
      this.setMouseup();
    },
    handleInteractionStart(e) {
      if ((!this.slices || this.slices.length <= 1))
        this.setDisableCustomEvents(true);
      else
        this.setDisableCustomEvents(false);
      this.handleInteractionMove(e);
    },
    handleHover(e){
      this.handleInteractionMove(e);
    },
    handleInteractionMove(e) {
      if (!this.refImgLocation) return;
      this.cursorClass = "";
      const { x, y } = this.refImgLocation(e.offsetX, e.offsetY);
      if (!this.isInBounds(x, y) || (!this.isInBoundSliceSelectorLocation(e.offsetX, e.offsetY) && !this.isDragging)) return;
      else this.checkForSlice(x, y);
    },
    handleWheel(e) {
      if (e.deltaY > 0) this.$emit("previous");
      else this.$emit("next");
    },
    isInBoundSliceSelectorLocation(x, y){
      let offset = viewOffset(this.refSize, this.boundingBoxSize);
      if (!withinViewBounds(x, y, this.refSize, offset)) {
        if(this.outOfBounds == Directions.Unknown && this.isDragging){
           this.outOfBounds = outOfBoundsDirection(x, y, this.refSize, offset)
        }       
        return false;
      } 
      return true;
    },
    isInBounds(x, y) {
      return x >= 0 && x <= 1 && y >= 0 && y <= 1;
    },
    checkForSlice(x, y) {
      if (
        (!this.slices || this.slices.length <= 1) &&
        (!this.composites || this.composites.length <= 1)
      )
        return;

      let { slice, composite } = this.isSpectralisRadialCircle ? this.radialCirclesPattern.getHoveredSlices(x, y, this.isDragging, this.outOfBounds) : this.sliceSelector.getHoveredSlices(x, y, this.isDragging, this.outOfBounds);
      if (slice !== null || composite !== null)
        this.cursorClass = POINTER_CLASSES.pointer;
      if (!this.isMousedown) return;

      if (slice !== null && this.sliceCurrentIndex !== slice)
        this.$emit("selectSlice", slice);

      if (composite !== null && this.compositeCurrentIndex !== composite)
        this.$emit("selectComposite", composite);
    },
    setDisableCustomEvents(val) {
      this.disableCustomEvents = val;
    }
  },
  watch: {
    isSpectralisCircle() {
      // to do use switch case when more conditions added
      if (this.isSpectralisRadialCircle) {
        this.radialCirclesPattern.updateSlices(this.slices);
      }
      else if(this.isSpectralisCircle){
        this.circularPattern.updateSlices(this.slices);
      }
      else{
        this.sliceSelector.updateSlices(this.slices);
      }   
    },
    isSpectralisRadial() {
      if (this.isSpectralisRadialCircle) {
        this.radialCirclesPattern.isRadial = !!this.isSpectralisRadial;
      }
      this.sliceSelector.isRadial = !!this.isSpectralisRadial;
    },
    isSpectralisRadialCircle() {
      // to do use switch case when more conditions added
      if (this.isSpectralisRadialCircle) {
        this.radialCirclesPattern.updateSlices(this.slices);
      }
      else if(this.isSpectralisCircle){
        this.circularPattern.updateSlices(this.slices);
      }
      else{
        this.sliceSelector.updateSlices(this.slices);
      }   
    },
    slices() {
      // to do use switch case when more conditions added
      if (this.isSpectralisRadialCircle) {
        this.radialCirclesPattern.updateSlices(this.slices);
      }
      else if(this.isSpectralisCircle){
        this.circularPattern.updateSlices(this.slices);
      }
      else{
        this.sliceSelector.updateSlices(this.slices);
      }   
    },
    sliceCurrent() {
      if (this.isSpectralisRadialCircle) {
        this.radialCirclesPattern.updatesliceCurrent(this.sliceCurrent);
      }
      else
        this.sliceSelector.updatesliceCurrent(this.sliceCurrent);
    },
    composites() {
      this.sliceSelector.updateCompositeSlices(this.composites);
    },
    compositeCurrent() {
      this.sliceSelector.updatecompositeCurrent(this.compositeCurrent);
    },
    sliceBounds() {
      if (this.isSpectralisRadialCircle)
        this.radialCirclesPattern.updateSliceBounds(this.slices);
      else
        this.sliceSelector.updateSliceBounds(this.sliceBounds);
      this.etdrs.updateBounds(this.sliceBounds);
    },
    compositeBounds() {
      this.sliceSelector.updateCompositeSliceBounds(this.compositeBounds);
    },
    palette() {
      this.paletteAnnotation.updateGradient(this.palette);
    },
    paletteMin() {
      this.paletteAnnotation.min = this.paletteMin;
    },
    paletteMax() {
      this.paletteAnnotation.max = this.paletteMax;
    },
    paletteStep() {
      this.paletteAnnotation.step = this.paletteStep;
    },
    drawAllSlices() {
      if (this.isSpectralisRadialCircle)
        this.radialCirclesPattern.updateDrawAllSlices(this.slices);
      else
        this.sliceSelector.updateDrawAllSlices(this.drawAllSlices);
    },
    showExtension() {
        this.sliceSelector.updateShowExtension(this.showExtension);
    },
    showBindingBox() {
      if (this.isSpectralisRadialCircle)
        this.radialCirclesPattern.updateShowBindingBox(this.slices);
      else
        this.sliceSelector.updateShowBindingBox(this.showBindingBox);
    },
    overlayAnalysis() {
      this.etdrs.updateThicknessAnalysis(this.overlayAnalysis);
    },
    overlayFoveaOffset() {
      this.etdrs.updateFoveaOffset(this.overlayFoveaOffset);
    },
    disableCustomEvents() {
      if (this.disableCustomEvents)
        this.$refs.octReference.style.touchAction = "auto";
      else
        this.$refs.octReference.style.touchAction = "none";
    }
  },
  mounted() {
    // to do use switch case when more conditions added
    if (this.isSpectralisCircle){
      this.circularPattern.updateSlices(this.slices);
    }
    else if (this.isSpectralisRadialCircle) {
      this.radialCirclesPattern.isRadial = true;
      this.radialCirclesPattern.drawAllSlices = this.drawAllSlices;
      this.radialCirclesPattern.showBindingBox = this.showBindingBox;
      this.radialCirclesPattern.updateSlices(this.slices);
      this.radialCirclesPattern.updateSliceBounds(this.sliceBounds);
      this.radialCirclesPattern.updatesliceCurrent(this.sliceCurrent);
    }
    else {
      this.sliceSelector.isRadial = !!this.isSpectralisRadial;
      this.sliceSelector.drawAllSlices = this.drawAllSlices;
      this.sliceSelector.showExtension = this.showExtension;
      this.sliceSelector.showBindingBox = this.showBindingBox;
      this.sliceSelector.updateSlices(this.slices);
      this.sliceSelector.updateSliceBounds(this.sliceBounds);
      this.sliceSelector.updatesliceCurrent(this.sliceCurrent);
      this.sliceSelector.updateCompositeSlices(this.composites);
      this.sliceSelector.updateCompositeSliceBounds(this.compositeBounds);
      this.sliceSelector.updatecompositeCurrent(this.compositeCurrent);
    }
    this.paletteAnnotation.updateGradient(this.palette);
    this.paletteAnnotation.min = this.paletteMin;
    this.paletteAnnotation.max = this.paletteMax;
    this.paletteAnnotation.step = this.paletteStep;
    this.etdrs.updateBounds(this.sliceBounds);
    this.etdrs.updateThicknessAnalysis(this.overlayAnalysis);
    this.etdrs.updateFoveaOffset(this.overlayFoveaOffset);
  }
};
</script>
